import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { PartyPopper } from 'lucide-react';
import { Logo } from '@gmp/shared-ui';
import { party } from '../net/client';
import { parse } from './router';
import { sound, usePrefs } from './prefs';
import { SiteFooter } from './Footer';
import { awayAtStart } from '../net/activity';

/**
 * The branded welcome screen: shown when the app is opened after a while away (never used on this device, or
 * not for 6 hours), while it finishes loading and connects to the game server, then "Tap to start" fades
 * into the app (Home, or the party you're rejoining). It picks up exactly where the instant loading screen
 * in index.html leaves off (same Pop, same bar, same colours).
 *
 * Never shown for deep links (/join/1234, /display/1234, /admin, /parties?open=…, /create…): those go
 * straight to their screen once the code arrives (index.html's loader covers the wait). Opens and reloads
 * within 6 hours of last using the app skip it (localStorage keeps when the app was last in use).
 */
/** Away at least this long and the welcome screen greets you again. */
export const WELCOME_AFTER_MS = 6 * 60 * 60 * 1000;
/** The splash stays at least this long so it never just flashes. */
const MIN_MS = 1400;
/** Give up waiting for the server after this long: Home shows its own "offline, retrying" line. */
const MAX_WAIT_MS = 6000;
/** Nobody tapped (a TV or laptop left alone): carry on into Home anyway. */
const AUTO_START_MS = 8000;

const ORBIT = ['panic', 'snap', 'nonsense', 'pizza', 'star-struck', 'orb'].map((d) => `/games/${d}/icon.webp`);
const CONFETTI = [
  { left: '7%', color: '#ff3d7f', delay: -0.4, dur: 4.2 },
  { left: '19%', color: '#ffd23f', delay: -2.1, dur: 3.6 },
  { left: '33%', color: '#00e5ff', delay: -1.2, dur: 4.8 },
  { left: '58%', color: '#ff3d7f', delay: -2.9, dur: 3.9 },
  { left: '71%', color: '#ffd23f', delay: -0.9, dur: 4.4 },
  { left: '84%', color: '#00e5ff', delay: -3.3, dur: 3.7 },
  { left: '94%', color: '#ff3d7f', delay: -1.7, dur: 4.6 },
];

function decide(): boolean {
  const route = parse().name;
  // A saved party is fine: the welcome fades into the rejoined room. Deep links skip it.
  return awayAtStart >= WELCOME_AFTER_MS && (route === 'home' || (route === 'party' && party.hasSavedRoom));
}

// Decided once, when the app first loads: in-app navigation never brings it back.
const showAtStart = typeof window !== 'undefined' && decide();

export function Welcome() {
  const [open, setOpen] = useState(showAtStart);
  return <AnimatePresence>{open && <WelcomeScreen key="welcome" onDone={() => setOpen(false)} />}</AnimatePresence>;
}

function WelcomeScreen({ onDone }: { onDone: () => void }) {
  const { status } = useSyncExternalStore(party.subscribe, party.getState);
  const prefs = usePrefs();
  const reduced = prefs.reducedMotion || (typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const [minDone, setMinDone] = useState(false);
  const [waitedOut, setWaitedOut] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    const a = setTimeout(() => setMinDone(true), reduced ? 500 : MIN_MS);
    const b = setTimeout(() => setWaitedOut(true), MAX_WAIT_MS);
    // The page under the splash must not scroll while it's up.
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      clearTimeout(a);
      clearTimeout(b);
      html.style.overflow = prev;
    };
  }, [reduced]);

  const online = status === 'online';
  const ready = minDone && (online || waitedOut);

  const start = () => {
    if (!ready || done.current) return;
    done.current = true;
    sound.unlock();
    sound.play('select');
    onDone();
  };
  const startRef = useRef(start);
  startRef.current = start;

  useEffect(() => {
    if (!ready) return;
    // A TV remote or keyboard: Enter or Space starts, wherever the focus is.
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      startRef.current();
    };
    window.addEventListener('keydown', key);
    const t = setTimeout(() => startRef.current(), AUTO_START_MS);
    return () => {
      window.removeEventListener('keydown', key);
      clearTimeout(t);
    };
  }, [ready]);

  // The bar: the app's code is here (40%), connecting (75%), connected (100%).
  const progress = online ? (minDone ? 100 : 90) : status === 'connecting' ? 72 : 55;
  const label = online ? 'Ready!' : status === 'connecting' ? 'Connecting to the game server…' : 'The game server is waking up…';

  return (
    <motion.div
      className="fixed inset-0 z-[70] stage-bg dots overflow-hidden flex flex-col select-none"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to Grudge Match Party"
      data-testid="welcome"
      data-ready={ready ? 'true' : 'false'}
      initial={false}
      exit={reduced ? { opacity: 0, transition: { duration: 0.15 } } : { opacity: 0, scale: 1.06, filter: 'blur(6px)', transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] } }}
      onClick={start}
    >
      {/* The party room, dimmed, with a warm spotlight behind Pop. */}
      <div aria-hidden className="absolute inset-0 bg-cover bg-center opacity-35" style={{ backgroundImage: 'url(/tv-lobby-bg.webp)' }} />
      <div aria-hidden className="absolute inset-0" style={{ background: 'radial-gradient(55% 45% at 50% 42%, rgba(255,210,63,0.16), transparent 70%), linear-gradient(to bottom, rgba(14,10,24,0.55), rgba(14,10,24,0.2) 40%, rgba(14,10,24,0.85))' }} />
      {!reduced && CONFETTI.map((c, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="absolute top-0 size-2.5 rounded-[3px] opacity-80"
          style={{ left: c.left, background: c.color }}
          initial={{ y: '-10vh', rotate: 0 }}
          animate={{ y: '110vh', rotate: 540 }}
          transition={{ duration: c.dur, delay: c.delay, repeat: Infinity, ease: 'linear' }}
        />
      ))}

      <div className="relative flex-1 min-h-0 grid place-items-center safe-pad safe-top">
        <div className="welcome-body flex flex-col items-center text-center gap-[clamp(10px,2.4vh,28px)]">
          <Art reduced={reduced} />
          <div className="flex flex-col items-center gap-[clamp(10px,2.2vh,26px)] welcome-copy">
            <motion.div className="welcome-logo" initial={reduced ? false : { scale: 0.85, rotate: -3, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 13, delay: 0.1 }}>
              <Logo size="lg" />
            </motion.div>
            <p className="text-paper/90 font-bold text-balance max-w-[26ch] text-[clamp(1rem,2.6vh,1.6rem)] leading-snug" data-testid="welcome-tagline">
              Party games for phones, TVs and friends far away. No app needed.
            </p>
            <div className="welcome-cta h-[clamp(64px,9vh,96px)] w-[min(78vw,360px)] grid place-items-center">
              <AnimatePresence mode="wait" initial={false}>
                {ready ? (
                  <motion.button
                    key="go"
                    // Focused for keyboards and TV remotes (not on touch screens, where it would just draw a focus ring).
                    ref={(el: HTMLButtonElement | null) => { if (el && !window.matchMedia?.('(pointer: coarse)').matches) el.focus({ preventScroll: true }); }}
                    className={`btn btn-gold w-full text-[clamp(1.2rem,2.8vh,1.7rem)] min-h-[clamp(56px,7.5vh,80px)] ${reduced ? '' : 'welcome-go'}`}
                    onClick={(e) => { e.stopPropagation(); start(); }}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.9 }}
                    animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: reduced ? 0.15 : 0.25 }}
                    data-testid="welcome-start"
                  >
                    <PartyPopper /> Tap to start
                  </motion.button>
                ) : (
                  <motion.div key="bar" className="w-full flex flex-col items-center gap-3" exit={{ opacity: 0, transition: { duration: 0.15 } }} role="status" aria-live="polite">
                    <div className="relative w-full h-3 rounded-full bg-white/10 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label="Loading">
                      <motion.div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#ff3d7f] via-[#ffd23f] to-[#00e5ff]" initial={{ width: '40%' }} animate={{ width: `${progress}%` }} transition={{ duration: reduced ? 0 : 0.6, ease: 'easeOut' }} />
                      {!reduced && (
                        <motion.i
                          aria-hidden
                          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/55 to-transparent"
                          animate={{ x: ['-120%', '320%'] }}
                          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                        />
                      )}
                    </div>
                    <p className="text-mute text-[clamp(0.9rem,2vh,1.2rem)]" data-testid="welcome-status">{label}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter className="welcome-foot relative safe-pad safe-bottom pt-1" />
    </motion.div>
  );
}

/** Pop bouncing on a squashing shadow, with game icons circling round like a party carousel. */
function Art({ reduced }: { reduced: boolean }) {
  return (
    <div className="welcome-art relative shrink-0" aria-hidden>
      <motion.div
        className="absolute inset-0"
        animate={reduced ? undefined : { rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
      >
        {ORBIT.map((src, i) => {
          const a = (i / ORBIT.length) * Math.PI * 2 - Math.PI / 2;
          return (
            <div key={src} className="absolute w-[24%] aspect-square -translate-x-1/2 -translate-y-1/2" style={{ left: `${50 + Math.cos(a) * 44}%`, top: `${50 + Math.sin(a) * 44}%` }}>
              <motion.img
                src={src}
                alt=""
                draggable={false}
                className="w-full h-full rounded-[28%] shadow-[0_8px_18px_rgba(0,0,0,0.5)] ring-2 ring-white/15"
                initial={reduced ? false : { scale: 0, opacity: 0 }}
                animate={reduced ? { opacity: 1 } : { scale: 1, opacity: 1, rotate: -360 }}
                transition={reduced ? undefined : { scale: { type: 'spring', stiffness: 260, damping: 14, delay: 0.15 + i * 0.07 }, opacity: { delay: 0.15 + i * 0.07 }, rotate: { duration: 28, repeat: Infinity, ease: 'linear' } }}
              />
            </div>
          );
        })}
      </motion.div>
      <div className="absolute left-1/2 top-1/2 w-[58%] -translate-x-1/2 -translate-y-[56%] flex flex-col items-center">
        <motion.img
          src="/mascot-sm.webp"
          alt=""
          width={320}
          height={301}
          draggable={false}
          className="w-full h-auto drop-shadow-[0_14px_18px_rgba(0,0,0,0.5)]"
          animate={reduced ? undefined : { y: ['0%', '-16%', '0%'], rotate: [-3, 3, -3], scaleY: [0.94, 1.03, 0.94], scaleX: [1.05, 0.98, 1.05] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: [0.3, 0.7, 0.4, 1] }}
          style={{ transformOrigin: '50% 100%' }}
        />
        <motion.span
          className="block w-[60%] h-[7%] min-h-2 -mt-[4%] rounded-[50%] bg-black/45"
          animate={reduced ? undefined : { scaleX: [1, 0.6, 1], opacity: [0.45, 0.2, 0.45] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: [0.3, 0.7, 0.4, 1] }}
        />
      </div>
    </div>
  );
}
